import Axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { setApiInstance } from '@linkie/api-client';
import toastClient from './toast-client';
import * as Sentry from '@sentry/react';
import { clearStaleAuth } from '../utils/clear-auth';
import { isCustomDomain } from '../utils/is-public-page-route';
import { buildLoginRedirectUrl } from '../utils/auth-redirect';

export const getApiBaseURL = () => {
  return isCustomDomain() ? '/api/v1' : process.env.API_HOST!;
};

function requestInterceptor(config: InternalAxiosRequestConfig) {
  if (config.headers) {
    config.headers.Accept = 'application/json';
    config.withCredentials = true;
  }

  return config;
}

const TELEMETRY_PATHS = [
  '/analytics/track-visitor',
  '/analytics/track-card-metric',
  '/analytics/track-email-collection',
];

function isNetworkError(error: any): boolean {
  return (
    error.isAxiosError &&
    (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.code === 'ECONNABORTED')
  );
}

function extractDetail(message?: string): string | undefined {
  if (!message) return undefined;
  const start = message.indexOf('{');
  if (start === -1) return undefined;
  try {
    return JSON.parse(message.slice(start)).detail;
  } catch {
    return undefined;
  }
}

export const api = Axios.create({
  baseURL: getApiBaseURL(),
  timeout: 10000,
});

api.interceptors.request.use(requestInterceptor);
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  (error) => {
    const url: string = error.config?.url ?? '';

    if (TELEMETRY_PATHS.some((path) => url.startsWith(path))) {
      return;
    }

    // Reject rather than resolve: swallowing this resolves the promise with `undefined`,
    // which React Query reports as a success and callers then dereference.
    if (isNetworkError(error)) {
      return Promise.reject(error);
    }

    const raw = error.response?.data?.message || error.message;

    const message = extractDetail(raw) || raw || 'An unexpected error occurred';

    const status: number | undefined = error.response?.status;

    const isIdentityProbe = url.startsWith('/auth/me');

    const isIdentityMismatch = status === 403 && url.startsWith('/auth/user/');

    const isOAuthHandoff = window.location.pathname.startsWith('/oauth/');

    if (!isIdentityProbe && !(status === 401 && isOAuthHandoff)) {
      toastClient.error(message);
    }

    if (status === 401 || isIdentityMismatch) {
      void clearStaleAuth().finally(() => {
        if (isIdentityProbe) return;

        if (window.location.pathname !== '/auth/login') {
          window.location.href = buildLoginRedirectUrl(window.location.pathname + window.location.search);
        }
      });
    }

    Sentry.captureException(error, {
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      },
    });

    return Promise.reject(error);
  },
);

setApiInstance(api);
